import { closestPointLine2 } from "geom/closestPoint";
import { vec2Dist } from "geom/vec2";

// This constant is sin(1°). This is used to determine whether two
// normals are sufficiently close to parallel to be considered
// parallel. Parallel normals (or normals close to parallel) trigger
// a mathematical degeneracy which needs special-case handling.
export const PARALLEL_NORMALS_SIN = Math.sin(Math.PI / 180);

export interface Vector2 {
  x: number;
  y: number;
}

export function projectOntoSegment(p: Vector2, a: Vector2, b: Vector2): Vector2 {
  const [x, y] = closestPointLine2(a.x, a.y, b.x, b.y, p.x, p.y, true);
  return { x, y };
}

export function distanceToSegment(p: Vector2, a: Vector2, b: Vector2): number {
  const proj = projectOntoSegment(p, a, b);
  return vec2Dist(p.x, p.y, proj.x, proj.y);
}

export function distance(a: Vector2, b: Vector2): number {
  return vec2Dist(a.x, a.y, b.x, b.y);
}

// Two expanding wave fronts move are moving outward along normals `a` and `b`. The wavefronts desire to stop at lengths `la` and `lb`. The wavefronts are joined a vertex, and that vertex needs to have a carefully selected velocity so that it moves with both wavefronts at once. This method cramerVelocity computes that velocity. The method is named crameVelocity because it uses Cramer's rule to solve a 2x2 linear system the get the correct velocity.

export function cramerVelocity(a: Vector2, la: number, b: Vector2, lb: number): Vector2 | null {
  const D = a.x * b.y - a.y * b.x;

  // A degenerate case occurs when when the wavefronts are parallel, or near parallel. There are two sub-cases in this degenerancy. If the wavefronts are moving at exactly equal speed then the vertex joining them can simply move at that exact speed. On the other hand, if the wavefronts are moving at different speeds then there is no solution. In this case no velocity can be assigned to the joining vertex, we return null and the system will turn this vertex into a step shape instead.

  if (Math.abs(D) < PARALLEL_NORMALS_SIN) {
    if (la === lb) {
      const dot = a.x * b.x + a.y * b.y;
      const phi = Math.atan2(D, dot);
      const c = Math.cos(phi / 2);
      const s = Math.sin(phi / 2);

      return {
        x: la * (a.x * c - a.y * s),
        y: la * (a.x * s + a.y * c),
      };
    }

    return null;
  }

  return {
    x: (la * b.y - lb * a.y) / D,
    y: (lb * a.x - la * b.x) / D,
  };
}
